/**
 * Pure helpers that turn the form catalog and per-widget submissions into
 * Forms WidgetBody props. No API calls live here.
 */

import { format } from 'date-fns';
import { submissionCreatedAtToMs } from '../../../../Form/utils/form-submission-date.util';
import { FORMS_WIDGET_TYPE } from '../../constants/reentry-planning.constants';

const RESPONSE_LABEL_FORMAT = 'MMM d, yyyy';
const EMPTY_SUBMISSIONS = Object.freeze([]);
const EMPTY_FORM_IDS = Object.freeze([]);

const isFormsUiWidget = (widget) => widget?.type === FORMS_WIDGET_TYPE;

const isPersistedWidget = (widget) => typeof widget?.id === 'string' && widget.isPending !== true;

const readWidgetFormIds = (widget) => widget?.props?.formIds ?? EMPTY_FORM_IDS;

const collectPersistedFormsWidgets = (sections = []) =>
  sections.flatMap((section) =>
    (section.widgets ?? []).filter((widget) => isFormsUiWidget(widget) && isPersistedWidget(widget)),
  );

/** True once the plan has at least one saved Forms widget (drives the catalog preload). */
export const hasPersistedFormsWidget = (sections = []) =>
  collectPersistedFormsWidgets(sections).length > 0;

/**
 * One ref per form attached to a persisted Forms widget, in widget then config order.
 * @returns {{ widgetId: string, formId: string }[]}
 */
export const collectFormsWidgetRefs = (sections = []) =>
  collectPersistedFormsWidgets(sections).flatMap((widget) =>
    readWidgetFormIds(widget).map((formId) => ({ widgetId: widget.id, formId })),
  );

/** Attached form ids of one mapped Forms widget; `[]` when it is missing or has none. */
export const findFormsWidgetFormIds = (sections = [], widgetId) => {
  const widget = sections
    .flatMap((section) => section.widgets ?? [])
    .find((candidate) => candidate.id === widgetId && isFormsUiWidget(candidate));
  return readWidgetFormIds(widget);
};

/** Stable key of the widget/form pairs that need responses; drives the submissions effect. */
export const buildFormsRefsKey = (refs = []) =>
  refs
    .filter((ref) => ref.formId)
    .map((ref) => `${ref.widgetId}:${ref.formId}`)
    .sort((left, right) => left.localeCompare(right))
    .join('|');

export const isActiveFormRow = (row) => Boolean(row?.active ?? row?.is_active);

/** Indexes catalog rows by string id for O(1) lookups. */
export const indexCatalogById = (rows = []) =>
  (Array.isArray(rows) ? rows : []).reduce((index, row) => {
    if (row?.id != null) {
      index[String(row.id)] = row;
    }
    return index;
  }, {});

/**
 * Active catalog rows as Add Forms modal options, keeping catalog order. Forms already attached
 * to the target widget are left out so a form is never added twice.
 */
export const toActiveFormOptions = (rows = [], attachedFormIds = EMPTY_FORM_IDS) =>
  (Array.isArray(rows) ? rows : [])
    .filter(isActiveFormRow)
    .map((row) => ({ id: String(row.id), name: row.name ?? '' }))
    .filter((option) => !attachedFormIds.includes(option.id));

/**
 * Resolves the selected form for the body. Unknown ids stay "loading" (no status)
 * until the catalog arrives, then become `unavailable`.
 *
 * @param {string | null} formId
 * @param {{ byId: Record<string, object>, loaded: boolean }} catalog
 */
export const resolveFormsWidgetForm = (formId, catalog) => {
  if (!formId) {
    return null;
  }

  const row = catalog.byId[formId];
  if (row) {
    return { id: formId, name: row.name ?? '', status: isActiveFormRow(row) ? 'active' : 'inactive' };
  }

  return catalog.loaded ? { id: formId, status: 'unavailable' } : { id: formId, name: '' };
};

const toSubmissionMs = (submission) => submissionCreatedAtToMs(submission?.created_at) ?? 0;

/** Newest submission first (approved default: chips ordered newest → oldest). */
export const sortSubmissionsNewestFirst = (submissions = []) =>
  [...submissions].sort((left, right) => toSubmissionMs(right) - toSubmissionMs(left));

/** Chip label such as "Jul 31, 2026" (Figma 716-11901); blank when the date is unusable. */
export const formatFormResponseLabel = (createdAt) => {
  const ms = submissionCreatedAtToMs(createdAt);
  return ms === null ? '' : format(new Date(ms), RESPONSE_LABEL_FORMAT);
};

export const toFormsWidgetResponses = (submissions = []) =>
  submissions.map((submission) => ({
    id: String(submission.id),
    label: formatFormResponseLabel(submission.created_at),
  }));

/** Finds one stored submission by response (chip) id. */
export const findSubmissionById = (submissions = [], responseId) =>
  submissions.find((submission) => String(submission.id) === String(responseId)) ?? null;

const hideFormsContent = (widget) => ({
  ...widget,
  props: { ...widget.props, contentHidden: true },
});

const toFormEntry = (widgetId, formId, context) => ({
  form: resolveFormsWidgetForm(formId, context.catalog),
  responses: toFormsWidgetResponses(
    context.submissionsByWidgetId[widgetId]?.[formId] ?? EMPTY_SUBMISSIONS,
  ),
});

const withFormsData = (widget, context) => ({
  ...widget,
  props: {
    ...widget.props,
    contentHidden: false,
    forms: readWidgetFormIds(widget).map((formId) => toFormEntry(widget.id, formId, context)),
    onResponseClick: (response, formId) =>
      context.onResponseClick(widget.id, formId, response.id),
  },
});

/**
 * Merges catalog + learner responses into one Forms widget as one entry per attached form
 * (config order), each with only its own responses. Without `form_view` the
 * shell stays but catalog, responses, and actions are hidden.
 */
export const enrichFormsWidget = (widget, context) => {
  if (!isFormsUiWidget(widget)) {
    return widget;
  }

  return context.canReadForms ? withFormsData(widget, context) : hideFormsContent(widget);
};

/**
 * @param {object[]} sections - Mapped UI sections
 * @param {{
 *   canReadForms: boolean,
 *   catalog: { byId: Record<string, object>, loaded: boolean },
 *   submissionsByWidgetId: Record<string, Record<string, object[]>>,
 *   onResponseClick: (widgetId: string, formId: string, responseId: string) => void,
 * }} context
 */
export const enrichFormsWidgetSections = (sections = [], context) =>
  sections.map((section) => ({
    ...section,
    widgets: (section.widgets ?? []).map((widget) => enrichFormsWidget(widget, context)),
  }));
